import type { Rarity } from './characters'

export type PropSlot = 'head' | 'face' | 'hair' | 'hand' | 'back' | 'accessory'

export type PropEffectKind =
  | 'coinMult'
  | 'scoreMult'
  | 'magnet'
  | 'vision'
  | 'shield'
  | 'slowmo'
  | 'speed'
  | 'flavor'

export interface PropEffect {
  kind: PropEffectKind
  value: number
  description: string
}

export interface PropDef {
  id: string
  name: string
  description: string
  slot: PropSlot
  rarity: Rarity
  price: { coins?: number; gems?: number }
  effects: PropEffect[]
  icon: string
  palette: { color: string; secondary: string; glow: string }
}

export const PROPS_CATALOG: PropDef[] = [
  {
    id: 'wizard-hat',
    name: 'Wizard Hat',
    description: 'A mystical pointed hat that reveals the way forward.',
    slot: 'head',
    rarity: 'rare',
    price: { coins: 500 },
    effects: [{ kind: 'vision', value: 2, description: 'Path hints are free to use' }],
    icon: 'wizardHat',
    palette: { color: '#7B3FF2', secondary: '#FFD700', glow: '#9D5CFF' },
  },
  {
    id: 'speed-sneakers',
    name: 'Lightning Sneakers',
    description: 'Rocket-powered shoes for blazing fast slides.',
    slot: 'accessory',
    rarity: 'common',
    price: { coins: 200 },
    effects: [{ kind: 'speed', value: 1.15, description: 'Slides travel 15% faster' }],
    icon: 'sneaker',
    palette: { color: '#FF6B35', secondary: '#FFEB3B', glow: '#FFA726' },
  },
  {
    id: 'shield-generator',
    name: 'Energy Shield',
    description: 'Portable force field that absorbs one hazard hit.',
    slot: 'back',
    rarity: 'rare',
    price: { coins: 750 },
    effects: [{ kind: 'shield', value: 1, description: 'Free shield at the start of every attempt' }],
    icon: 'shield',
    palette: { color: '#00BCD4', secondary: '#B2EBF2', glow: '#00E5FF' },
  },
  {
    id: 'magic-wand',
    name: 'Teleport Wand',
    description: 'An ancient wand humming with portal energy.',
    slot: 'hand',
    rarity: 'epic',
    price: { gems: 50 },
    effects: [{ kind: 'flavor', value: 1, description: 'Teleport pads spark brighter around you' }],
    icon: 'wand',
    palette: { color: '#E91E63', secondary: '#FCE4EC', glow: '#FF4081' },
  },
  {
    id: 'lucky-clover',
    name: 'Lucky Clover',
    description: 'Four-leaf clover that fattens every coin you bank.',
    slot: 'accessory',
    rarity: 'common',
    price: { coins: 300 },
    effects: [{ kind: 'coinMult', value: 1.5, description: 'Coins banked on a win +50%' }],
    icon: 'clover',
    palette: { color: '#4CAF50', secondary: '#8BC34A', glow: '#69F0AE' },
  },
  {
    id: 'ice-crown',
    name: 'Frost Crown',
    description: 'Icy crown that chills time itself.',
    slot: 'head',
    rarity: 'epic',
    price: { gems: 75 },
    effects: [{ kind: 'slowmo', value: 1, description: 'Slides play in readable slow motion' }],
    icon: 'crown',
    palette: { color: '#81D4FA', secondary: '#E1F5FE', glow: '#B3E5FC' },
  },
  {
    id: 'jetpack',
    name: 'Rocket Jetpack',
    description: 'High-tech thrusters with a serious exhaust plume.',
    slot: 'back',
    rarity: 'legendary',
    price: { gems: 150 },
    effects: [
      { kind: 'speed', value: 1.2, description: 'Slides travel 20% faster' },
      { kind: 'flavor', value: 1, description: 'Thruster plume trails every slide' },
    ],
    icon: 'jetpack',
    palette: { color: '#FF5722', secondary: '#FFC107', glow: '#FF6E40' },
  },
  {
    id: 'ghost-pendant',
    name: 'Phase Pendant',
    description: 'Ethereal pendant that shimmers between worlds.',
    slot: 'accessory',
    rarity: 'legendary',
    price: { gems: 200 },
    effects: [
      { kind: 'shield', value: 1, description: 'Free shield at the start of every attempt' },
      { kind: 'flavor', value: 1, description: 'Spectral shimmer while sliding' },
    ],
    icon: 'pendant',
    palette: { color: '#9C27B0', secondary: '#E1BEE7', glow: '#BA68C8' },
  },
  {
    id: 'magnet-glove',
    name: 'Magnetic Glove',
    description: 'Powerful electromagnet that attracts nearby coins.',
    slot: 'hand',
    rarity: 'common',
    price: { coins: 400 },
    effects: [{ kind: 'magnet', value: 2, description: 'Auto-collects coins within 2 tiles of each stop' }],
    icon: 'magnet',
    palette: { color: '#607D8B', secondary: '#CFD8DC', glow: '#90A4AE' },
  },
  {
    id: 'golden-trophy',
    name: 'Victory Trophy',
    description: 'Prestigious trophy that multiplies your score.',
    slot: 'hand',
    rarity: 'epic',
    price: { gems: 100 },
    effects: [{ kind: 'scoreMult', value: 2, description: 'Run score doubled' }],
    icon: 'trophy',
    palette: { color: '#FFD700', secondary: '#FFF59D', glow: '#FFEB3B' },
  },
  {
    id: 'viking-helmet',
    name: 'Viking Helmet',
    description: 'Sturdy horned helmet built to take a hit.',
    slot: 'head',
    rarity: 'common',
    price: { coins: 350 },
    effects: [{ kind: 'shield', value: 1, description: 'Free shield at the start of every attempt' }],
    icon: 'helmet',
    palette: { color: '#795548', secondary: '#BCAAA4', glow: '#8D6E63' },
  },
  {
    id: 'dragon-wings',
    name: 'Dragon Wings',
    description: 'Majestic wings that ride the heat of the lava fields.',
    slot: 'back',
    rarity: 'legendary',
    price: { gems: 250 },
    effects: [
      { kind: 'speed', value: 1.2, description: 'Slides travel 20% faster' },
      { kind: 'shield', value: 1, description: 'Free shield at the start of every attempt' },
    ],
    icon: 'wings',
    palette: { color: '#D32F2F', secondary: '#FFCDD2', glow: '#FF5252' },
  },
  {
    id: 'cool-glasses',
    name: 'Cool Glasses',
    description: 'Stylish shades that sharpen your read of the board.',
    slot: 'face',
    rarity: 'common',
    price: { coins: 150 },
    effects: [{ kind: 'vision', value: 1, description: 'Path hints stay visible twice as long' }],
    icon: 'glasses',
    palette: { color: '#212121', secondary: '#424242', glow: '#616161' },
  },
  {
    id: 'hipster-glasses',
    name: 'Hipster Glasses',
    description: 'Thick-rimmed frames for the intellectual slider.',
    slot: 'face',
    rarity: 'common',
    price: { coins: 200 },
    effects: [{ kind: 'scoreMult', value: 1.1, description: 'Run score +10%' }],
    icon: 'glasses',
    palette: { color: '#8B4513', secondary: '#A0522D', glow: '#CD853F' },
  },
  {
    id: 'afro-hair',
    name: 'Groovy Afro',
    description: 'A funky afro that shakes loose extra coins.',
    slot: 'hair',
    rarity: 'rare',
    price: { coins: 600 },
    effects: [{ kind: 'coinMult', value: 1.25, description: 'Coins banked on a win +25%' }],
    icon: 'hair',
    palette: { color: '#3E2723', secondary: '#5D4037', glow: '#6D4C41' },
  },
  {
    id: 'spiky-hair',
    name: 'Spiky Hair',
    description: 'Edgy spikes that cut through the air.',
    slot: 'hair',
    rarity: 'common',
    price: { coins: 250 },
    effects: [{ kind: 'speed', value: 1.1, description: 'Slides travel 10% faster' }],
    icon: 'hair',
    palette: { color: '#FFC107', secondary: '#FFD54F', glow: '#FFE082' },
  },
  {
    id: 'fancy-moustache',
    name: "Gentleman's Moustache",
    description: 'A distinguished moustache that commands respect.',
    slot: 'face',
    rarity: 'rare',
    price: { coins: 500 },
    effects: [{ kind: 'scoreMult', value: 1.3, description: 'Run score +30%' }],
    icon: 'moustache',
    palette: { color: '#3E2723', secondary: '#4E342E', glow: '#5D4037' },
  },
  {
    id: 'gamer-headphones',
    name: 'Gamer Headphones',
    description: 'RGB headphones that drop you into the zone.',
    slot: 'head',
    rarity: 'epic',
    price: { gems: 60 },
    effects: [{ kind: 'slowmo', value: 1, description: 'Slides play in readable slow motion' }],
    icon: 'headphones',
    palette: { color: '#1A1A1A', secondary: '#FF0080', glow: '#00FFFF' },
  },
  {
    id: 'pirate-beard',
    name: 'Pirate Beard',
    description: 'A rugged beard with a nose for treasure.',
    slot: 'face',
    rarity: 'epic',
    price: { gems: 40 },
    effects: [
      { kind: 'magnet', value: 3, description: 'Auto-collects coins within 3 tiles of each stop' },
      { kind: 'coinMult', value: 1.15, description: 'Coins banked on a win +15%' },
    ],
    icon: 'beard',
    palette: { color: '#5D4037', secondary: '#6D4C41', glow: '#795548' },
  },
]

export function getProp(id: string): PropDef | undefined {
  return PROPS_CATALOG.find((p) => p.id === id)
}

export const PROP_SLOTS: PropSlot[] = ['head', 'face', 'hair', 'hand', 'back', 'accessory']
